import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, MessageCircle, TrendingUp, Wallet, Zap, Trash2, Heart, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useApp } from '../../context/AppContext';
import { formatPrice } from '../../data/constants';
import { LOCALES } from '../../lib/i18n';
import { api, imgUrl, apiError } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { startCheckout, LOOKUP_KEYS } from '../../lib/payments';
import { ListingGrid } from '../ListingGrid';

export const StatCards = ({ stats }) => {
  const { t, lang } = useApp();
  const fmt = (n) => (n ?? 0).toLocaleString(LOCALES[lang]);
  const cards = [
    { id: 'listings', label: t('dash.listings'), v: fmt(stats?.listings), sub: `${fmt(stats?.active)} ${t('dash.status.active').toLowerCase()}`, icon: Wallet, color: '#0F8B5C' },
    { id: 'views', label: t('dash.views'), v: fmt(stats?.views), sub: t('dash.thisWeek'), icon: Eye, color: '#1F6FB2' },
    { id: 'favorites', label: t('dash.favorites'), v: fmt(stats?.favorites), sub: t('nav.favorites'), icon: Heart, color: '#E8814A' },
    { id: 'boosts', label: t('dash.activeBoosts'), v: fmt(stats?.boosted), sub: (stats?.plan || 'free').toUpperCase(), icon: Zap, color: '#F2B441' },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {cards.map((s) => (
        <div key={s.id} className="bg-white rounded-2xl border border-[#EFEAE0] p-5 hover:shadow-md transition-shadow" data-testid={`stat-card-${s.id}`}>
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${s.color}15`, color: s.color }}><s.icon className="w-5 h-5" /></div>
            <TrendingUp className="w-4 h-4 text-[#0F8B5C]" />
          </div>
          <div className="text-2xl font-black text-[#1A1A1A]">{s.v}</div>
          <div className="text-xs text-[#7A7468] mt-0.5">{s.label}</div>
          <div className="text-xs text-[#0F8B5C] font-semibold mt-1">{s.sub}</div>
        </div>
      ))}
    </div>
  );
};

const MyListingRow = ({ listing, onChanged }) => {
  const { t, currency, lang } = useApp();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    setBusy(true);
    try { await api.delete(`/listings/${listing.id}`); toast({ title: t('dash.deleted') }); onChanged(); }
    catch (e) { toast({ title: t('common.error'), description: apiError(e) }); setBusy(false); }
  };
  const markSold = async () => {
    setBusy(true);
    try { await api.put(`/listings/${listing.id}`, { status: 'sold' }); onChanged(); }
    catch (e) { toast({ title: t('common.error'), description: apiError(e) }); }
    finally { setBusy(false); }
  };
  const boost = async () => {
    setBusy(true);
    try { await startCheckout(LOOKUP_KEYS.boost, { listingId: listing.id, userId: listing.sellerId }); }
    catch (e) { toast({ title: t('post.payError'), description: apiError(e) }); setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-[#EFEAE0] p-3 hover:shadow-md transition-shadow" data-testid={`my-listing-${listing.id}`}>
      <Link to={`/listing/${listing.id}`}><img src={imgUrl(listing.images[0])} alt="" className="w-full aspect-[4/3] object-cover rounded-lg" /></Link>
      <div className="mt-3">
        <div className="flex items-center justify-between">
          <div className="font-bold text-[#0F8B5C]">{formatPrice(listing.price, listing.currency, currency, LOCALES[lang])}</div>
          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${listing.status === 'active' ? 'bg-[#0F8B5C]/10 text-[#0A6E47]' : 'bg-[#EFEAE0] text-[#7A7468]'}`}>{t(`dash.status.${listing.status}`)}</span>
        </div>
        <div className="text-sm font-medium text-[#1A1A1A] line-clamp-1">{listing.title}</div>
        <div className="flex items-center justify-between mt-2 text-xs text-[#7A7468]">
          <span>{listing.views} {t('dash.viewsLabel')}</span>
          {listing.boosted ? <span className="text-[#F2B441] font-bold">⚡ {t('dash.boosted')}</span>
            : <button onClick={boost} disabled={busy} className="text-[#0F8B5C] font-semibold hover:underline" data-testid={`boost-listing-${listing.id}`}>{t('dash.boost')}</button>}
        </div>
        <div className="flex gap-2 mt-3">
          {listing.status === 'active' && <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" disabled={busy} onClick={markSold} data-testid={`sold-listing-${listing.id}`}>{t('dash.markSold')}</Button>}
          <Button size="sm" variant="outline" className="h-8 text-xs text-[#E8814A] hover:text-[#E8814A]" disabled={busy} onClick={remove} data-testid={`delete-listing-${listing.id}`}>{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}</Button>
        </div>
      </div>
    </div>
  );
};

export const ListingsTab = ({ listings, loading, onChanged }) => {
  const { t } = useApp();
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#0F8B5C]" /></div>;
  if (!listings?.length) {
    return (
      <div className="text-center py-12 text-[#7A7468]" data-testid="dashboard-no-listings">
        <p>{t('dash.noListings')}</p>
        <Link to="/post"><Button className="mt-4 bg-[#0F8B5C] hover:bg-[#0A6E47] text-white">{t('dash.newListing')}</Button></Link>
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dashboard-listings">
      {listings.map((l) => <MyListingRow key={l.id} listing={l} onChanged={onChanged} />)}
    </div>
  );
};

export const FavoritesTab = ({ listings, loading, error, reload }) => {
  const { t } = useApp();
  return <ListingGrid listings={listings} loading={loading} error={error} onRetry={reload} compact cols="grid-cols-2 lg:grid-cols-3" emptyText={t('dash.noFavs')} testId="dashboard-favorites" />;
};

export const MessagesTab = () => {
  const { t } = useApp();
  return (
    <div className="text-center py-12 text-[#7A7468]" data-testid="dashboard-messages">
      <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-40" />
      <p className="font-medium">{t('dash.emptyInbox')}</p>
    </div>
  );
};
